import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { InputClearValue, InputSeparator, TextBox, TextBoxProps } from '@progress/kendo-react-inputs';
import { DatePicker, DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { SvgIcon } from '@progress/kendo-react-common';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { useLocalization } from '@progress/kendo-react-intl';
import { arrowRotateCwIcon, downloadIcon, filterClearIcon, xIcon } from '@progress/kendo-svg-icons';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import moment from 'moment';
import { enMessages } from '../../../../../messages/en-US';
import SelectAllMultiSelectTree from '../../../../Shared/Components/SelectAllMultiSelectTree';
import DashboardPanel from '../../../../Shared/Components/DashboardPanel';
import type { EntityNoteCategory, Note } from './EntityNotesPanel';
import { NOTE_CATEGORY_KEYS } from './noteCategories';
import { useNoteCategories, useNoteEditing } from './noteHooks';
import NoteCard, { InternalChip } from './NoteCard';

/**
 * A `Note` plus the view-only fields the timeline needs for header/search/export. Each tab spreads
 * its `Note`s and adds these, so the view stays agnostic about where the notes come from.
 * `entityType`/`userId` already live on `Note`; tabs may re-assign `entityType` to a normalized value.
 */
export interface TimelineViewNote extends Note {
  /** Identity header rendered top-left of the card (entity name/serial/asset, with icon). */
  header: ReactNode;
  /** Lowercased haystack for the search box (entity name, serial, author, categories, body…). */
  searchText: string;
  /** Flat row for the Excel export — keys must match the `exportColumns` passed in. */
  exportRow: Record<string, unknown>;
}

export type DateRange = { start: Date | null; end: Date | null };

/** Default timeline window — last 30 days. Tabs seed their server query with this. */
export const defaultNotesDateRange = (): DateRange => ({
  start: moment().subtract(30, 'days').startOf('day').toDate(),
  end: moment().endOf('day').toDate(),
});

interface Props {
  /** Notes already sorted (newest first) and mapped to the view shape. */
  notes: TimelineViewNote[];
  /** Entity-type filter options (value must match `note.entityType`). */
  entityTypeOptions: { value: string; text: string }[];
  /** Distinct asset names for the optional Asset filter (matched against `note.assetName`). Omit to hide it. */
  assetOptions?: string[];
  /** `<ExcelExportColumn />` set whose fields match `note.exportRow` keys. */
  exportColumns: ReactNode;
  exportFileName: string;
  /** Date range is owned by the tab (it drives the server query); the view just renders the pickers. */
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  isLoading: boolean;
  isError?: boolean;
  errorMessage?: string;
  isFetching: boolean;
  onRefresh: () => void;
  onSave: (
    noteId: string,
    input: { description: string; categories: EntityNoteCategory[]; isInternalOnly: boolean },
  ) => void;
  onDelete: (noteId: string) => void;
  currentUserId: string;
}

/**
 * Shared notes timeline surface used by the per-asset and dashboard-wide Notes tabs. Owns the
 * filter bar (search / entity / category / date range), the Excel export wiring, inline edit/delete,
 * and the timeline rendering. The tabs only supply the data, the per-row header, and export columns.
 */
export default function NotesTimelineView({
  notes,
  entityTypeOptions,
  assetOptions,
  exportColumns,
  exportFileName,
  dateRange,
  onDateRangeChange,
  isLoading,
  isError,
  errorMessage,
  isFetching,
  onRefresh,
  onSave,
  onDelete,
  currentUserId,
}: Props) {
  const localizationService = useLocalization();
  const { categories, categoryLabel, renderCategoryItem } = useNoteCategories();

  const searchPlaceholderL = localizationService.toLanguageString('custom.searchNotes', enMessages.custom.searchNotes);
  const noNotesMatchL = localizationService.toLanguageString('custom.noNotesMatchSearch', enMessages.custom.noNotesMatchSearch);
  const noNotesYetL = localizationService.toLanguageString('custom.noNotesYet', enMessages.custom.noNotesYet);
  const cardLabels = {
    createdBy: localizationService.toLanguageString('custom.createdBy', enMessages.custom.createdBy),
    edit: localizationService.toLanguageString('custom.edit', enMessages.custom.edit),
    delete: localizationService.toLanguageString('custom.delete', enMessages.custom.delete),
    cancel: localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel),
    save: localizationService.toLanguageString('custom.save', enMessages.custom.save),
  };

  const allEntityValues = useMemo(() => entityTypeOptions.map((o) => o.value), [entityTypeOptions]);

  // Filters. Date range is controlled by the tab (server-side); the rest are local (client-side).
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>(allEntityValues);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<EntityNoteCategory[]>([...NOTE_CATEGORY_KEYS]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);

  // Asset options load async — default to "all selected" once they arrive (only the first time,
  // so the user's later (de)selection isn't overwritten on re-render).
  const assetsInitialized = useRef(false);
  useEffect(() => {
    if (!assetsInitialized.current && assetOptions && assetOptions.length > 0) {
      setSelectedAssets(assetOptions);
      assetsInitialized.current = true;
    }
  }, [assetOptions]);

  const entityFilterOptions = entityTypeOptions.map((o) => ({ id: o.value, text: o.text }));
  const categoryFilterOptions = categories.map((c) => ({ id: c.value, text: c.text }));
  const assetFilterOptions = (assetOptions ?? []).map((name) => ({ id: name, text: name }));

  const editing = useNoteEditing({ categories, notes, onSave, onDelete });

  const filteredNotes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return notes.filter((n) => {
      if (dateRange.start && n.createdAt < dateRange.start) return false;
      if (dateRange.end && n.createdAt > dateRange.end) return false;
      if (selectedEntityIds.length > 0 && !selectedEntityIds.includes(n.entityType)) return false;
      if (selectedCategoryIds.length > 0 && !n.categories.some((c) => selectedCategoryIds.includes(c))) return false;
      // Only filter once the user narrows the asset selection — "all selected" shows everything,
      // so notes whose asset isn't in the machine list (renamed/removed) are never silently dropped.
      if (assetOptions && selectedAssets.length < assetOptions.length && !selectedAssets.includes(n.assetName ?? ''))
        return false;
      if (q && !n.searchText.includes(q)) return false;
      return true;
    });
  }, [notes, searchQuery, dateRange, selectedEntityIds, selectedCategoryIds, assetOptions, selectedAssets]);

  const excelExportRef = useRef<ExcelExport | null>(null);
  const exportData = useMemo(() => filteredNotes.map((n) => n.exportRow), [filteredNotes]);

  // Clears the filters only — the search box keeps its own inline clear (the "x").
  const clearAllFilters = () => {
    onDateRangeChange(defaultNotesDateRange());
    setSelectedEntityIds(allEntityValues);
    setSelectedCategoryIds([...NOTE_CATEGORY_KEYS]);
    setSelectedAssets(assetOptions ?? []);
  };

  const hasActiveFilters =
    selectedEntityIds.length < allEntityValues.length ||
    selectedCategoryIds.length < NOTE_CATEGORY_KEYS.length ||
    (!!assetOptions && selectedAssets.length < assetOptions.length);

  // Toolbar wraps on narrow screens; the search box goes full-width on mobile.
  const toolbar = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
      <TextBox
        placeholder={searchPlaceholderL}
        value={searchQuery as TextBoxProps['value']}
        onChange={(e) => setSearchQuery(typeof e.target?.value === 'string' ? e.target.value : '')}
        style={{ width: 220, height: 30 }}
        suffix={() => (
          <>
            {searchQuery !== '' && (
              <InputClearValue onClick={() => setSearchQuery('')}>
                <SvgIcon icon={xIcon} />
              </InputClearValue>
            )}
            <InputSeparator />
          </>
        )}
      />
      <SelectAllMultiSelectTree<string>
        groupKey="entity"
        tagLabel="Entity"
        selectAllLabel="Select All"
        options={entityFilterOptions}
        value={selectedEntityIds}
        onChange={setSelectedEntityIds}
      />
      <SelectAllMultiSelectTree<EntityNoteCategory>
        groupKey="category"
        tagLabel="Category"
        selectAllLabel="Select All"
        options={categoryFilterOptions}
        value={selectedCategoryIds}
        onChange={setSelectedCategoryIds}
      />
      {assetOptions && assetOptions.length > 0 && (
        <SelectAllMultiSelectTree<string>
          groupKey="asset"
          tagLabel="Asset"
          selectAllLabel="Select All"
          filterable
          options={assetFilterOptions}
          value={selectedAssets}
          onChange={setSelectedAssets}
        />
      )}
      {hasActiveFilters && (
        <Button svgIcon={filterClearIcon} themeColor="base" onClick={clearAllFilters}>
          Clear
        </Button>
      )}
      <div className="flex items-center gap-x-2">
        <DatePicker
          value={dateRange.start}
          onChange={(e: DatePickerChangeEvent) => onDateRangeChange({ ...dateRange, start: e.value })}
          max={dateRange.end || undefined}
          width="150px"
        />
        <span className="opacity-60">-</span>
        <DatePicker
          value={dateRange.end}
          onChange={(e: DatePickerChangeEvent) => onDateRangeChange({ ...dateRange, end: e.value })}
          min={dateRange.start || undefined}
          max={moment().toDate()}
          width="150px"
        />
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span className="flex items-center gap-1">
            <Button
              svgIcon={arrowRotateCwIcon}
              onClick={onRefresh}
              disabled={isFetching}
              title={localizationService.toLanguageString('custom.refresh', 'Refresh')}
            />
            <Button
              svgIcon={downloadIcon}
              disabled={exportData.length === 0}
              onClick={() => excelExportRef.current?.save()}
              title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <DashboardPanel toolbar={toolbar}>
      <ExcelExport data={exportData} ref={excelExportRef} fileName={exportFileName}>
        {exportColumns}
      </ExcelExport>

      {/* Timeline — gray inset area inside the white panel, matching the Overview tile area tone. */}
      <div className="flex-1 overflow-y-auto bg-[#fafafa] p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-center text-sm opacity-70">Loading…</div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-sm">
            <div className="text-red-600">Failed to load notes.</div>
            <div className="mt-1 text-xs opacity-60">{errorMessage ?? 'Unknown error'}</div>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-center text-sm opacity-70">{noNotesYetL}</div>
        ) : filteredNotes.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-center text-sm opacity-70">{noNotesMatchL}</div>
        ) : (
          <Tooltip anchorElement="target" position="top" parentTitle={true}>
            <div className="max-w-[1000px]">
              {filteredNotes.map((n) => {
                const isOwn = n.userId === currentUserId && currentUserId.length > 0;
                return (
                  // Timeline row: date/time (+ internal chip) gutter, connector, then the note card.
                  <div key={n.id} className="flex gap-3">
                    <div className="w-20 shrink-0 pt-1 text-right text-xs leading-tight text-gray-500">
                      <div className="font-medium text-gray-700">{moment(n.createdAt).format('DD MMM YYYY')}</div>
                      <div>{moment(n.createdAt).format('HH:mm')}</div>
                      {n.isInternalOnly && (
                        <div className="mt-1 flex justify-end">
                          <InternalChip />
                        </div>
                      )}
                    </div>
                    <div className="flex w-3 shrink-0 flex-col items-center">
                      <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border-2 border-gray-300 bg-white" />
                      <span className="mt-1 w-px flex-1 bg-gray-200" />
                    </div>
                    <div className="min-w-0 flex-1 pb-4">
                      <NoteCard
                        note={n}
                        header={n.header}
                        categories={categories}
                        categoryLabel={categoryLabel}
                        isOwn={isOwn}
                        authorIsInternal={!!n.isGbUser}
                        isBeingEdited={editing.editingId === n.id}
                        editingDescription={editing.editingDescription}
                        editingCategories={editing.editingCategories}
                        editingInternalOnly={editing.editingInternalOnly}
                        onChangeEditingDescription={editing.setEditingDescription}
                        onChangeEditingCategories={editing.setEditingCategories}
                        onChangeEditingInternalOnly={editing.setEditingInternalOnly}
                        onStartEdit={() =>
                          editing.startEdit({
                            id: n.id,
                            description: n.description,
                            categories: n.categories,
                            isInternalOnly: n.isInternalOnly,
                          })
                        }
                        onCancelEdit={editing.cancelEdit}
                        onSaveEdit={editing.saveEdit}
                        onDelete={() => editing.deleteNote(n.id)}
                        renderCategoryItem={renderCategoryItem}
                        labels={cardLabels}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Tooltip>
        )}
      </div>
    </DashboardPanel>
  );
}
